import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type { SupabaseAuth } from '../auth/supabase';
import { useTurnstile } from '../auth/turnstile';
import { Link } from 'react-aria-components';
import { Wordmark } from '../components/Mark';
import { Banner } from '../ui/Banner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { InputField } from '../ui/Input';
import { Stack } from '../ui/Stack';
import { useDocumentTitle } from '../useDocumentTitle';

/** One set of legal pages, on the marketing site, covers the site and the app. */
const TERMS_URL = 'https://reportpad.app/terms';
const PRIVACY_URL = 'https://reportpad.app/privacy';
const LEGAL_LINK =
  'text-ink underline cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';

const RATE_LIMIT_CODES =['over_email_send_rate_limit', 'over_request_rate_limit'];

function isRateLimit(cause: unknown): boolean {
  const e = (cause ?? {}) as { status?: unknown; code?: unknown };
  return e.status === 429 || (typeof e.code === 'string' && RATE_LIMIT_CODES.includes(e.code));
}

function plainSupabaseError(cause: unknown): string {
  const e = (cause ?? {}) as { message?: unknown };
  if (isRateLimit(cause)) return 'Too many sign-in emails. Wait a minute, then try again.';
  return typeof e.message === 'string' && e.message ? e.message : 'Could not sign in. Try again.';
}

const INVALID_CREDENTIALS = 'Email or password is incorrect.';

function plainPasswordError(cause: unknown): string {
  const e = (cause ?? {}) as { status?: unknown; code?: unknown };
  // A 400 with no code is Supabase's bare bad-credentials reply; a coded 400 (captcha_failed,
  // email_not_confirmed, ...) keeps its own message.
  if (e.code === 'invalid_credentials' || (e.status === 400 && typeof e.code !== 'string')) return INVALID_CREDENTIALS;
  // Password mode sends no email, so its rate limit is about attempts.
  if (isRateLimit(cause)) return 'Too many sign-in attempts. Wait a minute, then try again.';
  return plainSupabaseError(cause);
}

/** The signed-out screen in accounts mode: an emailed link, email and password, or Google. */
export function SignInRoute(props: {
  auth: SupabaseAuth;
  turnstileSiteKey: string | null;
  notice: string | null;
  /** Shows email + password in place of the emailed link (`/login?method=password`). */
  passwordSignIn?: boolean;
  /** Runs once Supabase accepts the password; the app still has to exchange the session. */
  onPasswordSignedIn?(): void;
}): ReactElement {
  useDocumentTitle('Sign in');
  const passwordSignIn = props.passwordSignIn ?? false;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const needsCaptcha = props.turnstileSiteKey !== null;
  const widget = useTurnstile(props.turnstileSiteKey, setCaptcha);

  async function sendLink(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await props.auth.signInWithEmail(email.trim(), captcha);
      setSentTo(email.trim());
    } catch (cause) {
      setError(plainSupabaseError(cause));
    } finally {
      widget.reset();
      setCaptcha(null);
      setSending(false);
    }
  }

  async function submitPassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await props.auth.signInWithPassword(email.trim(), password, captcha);
      props.onPasswordSignedIn?.();
    } catch (cause) {
      setError(plainPasswordError(cause));
    } finally {
      widget.reset();
      setCaptcha(null);
      setSending(false);
    }
  }

  async function google(): Promise<void> {
    setError(null);
    try {
      await props.auth.signInWithGoogle();
    } catch (cause) {
      setError(plainSupabaseError(cause));
    }
  }

  const message = error ?? widget.loadError ?? props.notice;

  return (
    <div className="min-h-screen flex flex-col bg-desk font-ui">
      <header className="bg-nav px-6 py-3">
        <Link
          href="/"
          aria-label="ReportPad home"
          className="no-underline text-on-nav-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-nav-strong"
        >
          <Wordmark tone="dark" />
        </Link>
      </header>
      <main className="flex-1 grid place-items-center p-6">
        <Card className="w-full max-w-sm">
          <Stack gap={4}>
            <div>
              <Wordmark />
              <h1 className="sr-only">Sign in</h1>
            </div>

            {message ? <Banner tone="danger" title={message} /> : null}

            {sentTo ? (
              <p role="status" className="m-0 text-md text-ink">
                Check your email. We sent a sign-in link to {sentTo}.
              </p>
            ) : (
              <>
                <form onSubmit={(event) => void (passwordSignIn ? submitPassword(event) : sendLink(event))}>
                  <Stack gap={4}>
                    <InputField
                      label="Email"
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={setEmail}
                      isRequired
                      autoFocus
                    />
                    {passwordSignIn ? (
                      <InputField
                        label="Password"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={setPassword}
                        isRequired
                      />
                    ) : null}
                    {widget.element}
                    <Button
                      type="submit"
                      variant="primary"
                      isDisabled={
                        !email.trim() || (passwordSignIn && !password) || (needsCaptcha && !captcha) || sending
                      }
                    >
                      {passwordSignIn ? 'Sign in' : 'Email me a sign-in link'}
                    </Button>
                  </Stack>
                </form>
                <Button onPress={() => void google()}>Continue with Google</Button>
                <p className="m-0 text-sm text-muted">
                  By continuing, you agree to the{' '}
                  <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={LEGAL_LINK}>
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={LEGAL_LINK}>
                    Privacy Policy
                  </Link>
                  .
                </p>
              </>
            )}
          </Stack>
        </Card>
      </main>
    </div>
  );
}
